'use no memo';

import {DataStatus} from 'platform/components';

import {useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';

import {any, head, isNotNil} from 'ramda';
import {isTrue} from 'ramda-adjunct';

import {Main} from '@dms/shared';
import {
  useThunkDispatch,
  getCondition,
  InspectionType,
  VehicleInspection,
  selectCarAudit,
  updateAuditData,
  getInitialAuditData,
} from '@dms/teas';

import {useFetchAuditStructure} from '../../hooks/useFetchAuditStructure';

export const Conditions = () => {
  const dispatch = useThunkDispatch();

  const {id: vehicleId} = useParams();

  const {fetchAuditStructure} = useFetchAuditStructure();

  const {loading} = useSelector(selectCarAudit);

  // Track which vehicleId is currently being loaded to prevent duplicate loads
  const loadingVehicleIdRef = useRef<string | null>(null);
  // Local loading state to track the full loading process (including manual steps)
  const [isLocalLoading, setIsLocalLoading] = useState(false);

  const isAuditLoading = any(isTrue, [
    isLocalLoading,
    loading.getCondition,
    loading.getAuditStructure,
    loading['vehicleCatalogue/getCustomTenantCatalogue'],
  ]);

  useEffect(() => {
    // Flag to track if this effect instance is stale (component unmounted or vehicleId changed)
    let isCancelled = false;

    if (isNotNil(vehicleId)) {
      // Skip if already loading this exact vehicle (prevent duplicate loads)
      if (loadingVehicleIdRef.current === vehicleId) {
        return;
      }

      loadingVehicleIdRef.current = vehicleId;
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsLocalLoading(true);

      dispatch(
        getCondition.action({
          requestBody: {vehicleIds: [vehicleId]},
          inspectionType: InspectionType.vehicleCondition,
          withoutReducer: true,
        })
      )
        .unwrap()
        .then((response) => {
          if (isCancelled) {
            return;
          }

          if (isNotNil(response)) {
            const auditData = getInitialAuditData(head(response));
            dispatch(updateAuditData({auditData}));
            fetchAuditStructure(head(response));
          }
        })
        .finally(() => {
          if (!isCancelled) {
            setIsLocalLoading(false);
          }
        });
    }

    return () => {
      isCancelled = true;
      loadingVehicleIdRef.current = null;
      setIsLocalLoading(false);
    };
  }, [vehicleId]);

  return (
    <Main>
      <DataStatus isLoading={isAuditLoading} minHeight={100}>
        <VehicleInspection shouldUseAuditIdForUpdateTemplate />
      </DataStatus>
    </Main>
  );
};
